import React, { useState, useEffect } from 'react';
import { Star, AlignLeft, Moon, Clock } from 'lucide-react';

interface RecoveryBodyProps {
    data: any;
    updatePayload: (updates: any) => void;
}

/**
 * RecoveryBody: Specialized domain editor for Sleep/Recovery events.
 * Optimized with local state to prevent typing lag.
 * SHADOW VERSION (.tsx)
 */
export const RecoveryBody: React.FC<RecoveryBodyProps> = ({ data, updatePayload }) => {
    const { payload = {} } = data;
    const rating = payload.quality_rating || 0;
    
    // 1. LOCAL STATE BUFFER
    const [localNotes, setLocalNotes] = useState<string>(payload.notes || "");

    // 2. Sync local state if data from parent changes
    useEffect(() => {
        setLocalNotes(payload.notes || "");
    }, [payload.notes]);

    // Calculate Duration
    const start = new Date(`${data.startDate}T${data.startTime}:00`).getTime();
    const end = new Date(`${data.endDate}T${data.endTime}:00`).getTime();
    const diff = end - start;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);

    const labelStyle: React.CSSProperties = { fontSize: '10px', fontWeight: '900', color: '#444', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '6px' };
    const inputStyle: React.CSSProperties = { all: 'unset', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid #222', borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', width: '100%', boxSizing: 'border-box' };

    return (
        <div className="animate-in fade-in slide-in-from-top-2">
            {/* 1. Duration Visual - HARDENED LAYOUT */}
            <div style={{ 
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '20px 0', backgroundColor: 'rgba(59, 130, 246, 0.03)', borderRadius: '16px', 
                border: '1px solid rgba(59, 130, 246, 0.1)', marginBottom: '25px' 
            }}>
                <div style={{ fontSize: '9px', fontWeight: '950', color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px', opacity: 0.8 }}>
                    Recovery Duration
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Moon size={22} color="#3b82f6" fill="#3b82f6" style={{ opacity: 0.8 }} />
                    <div style={{ fontSize: '28px', fontWeight: '900', color: '#fff', fontFamily: 'monospace', letterSpacing: '-1px' }}>
                        {h}h {m.toString().padStart(2, '0')}'
                    </div>
                </div>

                {diff < 0 && <div style={{ color: '#ef4444', fontSize: '9px', fontWeight: '900', marginTop: '10px' }}>INVALID TEMPORAL RANGE</div>}
            </div>

            {/* 2. Quality Rating */}
            <div style={{ marginBottom: '25px' }}>
                <div style={labelStyle}><Star size={12} color="#f29b11" /> Quality Rating</div>
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', padding: '5px 0' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            onClick={() => updatePayload({ quality_rating: star })}
                            style={{ all: 'unset', cursor: 'pointer', transition: 'transform 0.1s ease' }}
                            className="hover-scale"
                        >
                            <Star 
                                size={32} 
                                color={star <= rating ? "#f29b11" : "#222"} 
                                fill={star <= rating ? "#f29b11" : "transparent"} 
                                strokeWidth={2.5}
                            />
                        </button>
                    ))}
                </div>
            </div>

            {/* 3. Wake-up Notes - OPTIMIZED */}
            <div style={{ marginBottom: '30px' }}>
                <div style={labelStyle}><AlignLeft size={12} color="#3b82f6" /> Recovery Notes</div>
                <textarea 
                    placeholder="How did you feel upon waking?"
                    value={localNotes}
                    onChange={(e) => setLocalNotes(e.target.value)}
                    onBlur={() => updatePayload({ notes: localNotes })}
                    style={{ ...inputStyle, height: '100px', resize: 'none', fontSize: '13px', lineHeight: '1.6', fontStyle: 'italic' }}
                />
            </div>

            <div style={{ display: 'flex', gap: '10px', padding: '15px', backgroundColor: 'rgba(255,255,255,0.01)', borderRadius: '12px', border: '1px solid #111' }}>
                <Clock size={16} color="#333" style={{ flexShrink: 0 }} />
                <p style={{ fontSize: '11px', color: '#444', margin: 0, lineHeight: '1.4' }}>
                    AI Analysis will prioritize these notes to correlate recovery quality with your future performance outputs.
                </p>
            </div>
        </div>
    );
};
